import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Text as ChakraText,
} from '@chakra-ui/react'
import { useRef } from 'react'
import { Field } from 'formik'
import { useTranslation } from 'react-i18next'
import { FiFile } from 'react-icons/fi'

interface Props {
  isDisabled?: boolean
}

const GoogleKeyFileUpload = ({ isDisabled }: Props): React.ReactElement => {
  const { t } = useTranslation()

  const inputFileRef = useRef<HTMLInputElement>(null)

  const onButtonClick = (): void => {
    inputFileRef.current?.click()
  }

  const handleFileChange = ({
    event,
    form,
  }: {
    event: React.ChangeEvent<HTMLInputElement>
    form: any
  }): void => {
    if (event.target.files === null) {
      return
    }
    const file = event.target.files[0]
    const fileReader = new FileReader()
    fileReader.readAsText(file, 'UTF-8')

    fileReader.onload = e => {
      try {
        if (typeof e.target?.result !== 'string') {
          throw new Error()
        }
        const jsonString: string = e.target.result
        const googleJson = JSON.parse(jsonString)
        if (!googleJson.private_key || !googleJson.client_email) {
          throw new Error()
        }
        const privateKey = googleJson.private_key
        const clientEmail = googleJson.client_email

        form.setFieldValue('privateKey', privateKey)
        form.setFieldValue('clientEmail', clientEmail)
      } catch (err) {
        form.setFieldError(
          'googleKeyFile',
          'Invalid File. Make sure the private key and client email fields are present in the JSON file',
        )
      }
    }
  }

  return (
    <Field name="googleKeyFile">
      {({ form }) => (
        <FormControl isInvalid={Boolean(form.errors.googleKeyFile)}>
          <FormLabel htmlFor="googleKeyFile">
            {t('google_key_file_label', 'Google Service Account Key JSON File')}
          </FormLabel>
          <ChakraText paddingBottom="0.8em">
            {t(
              'google_key_file_description',
              'The JSON file you downloaded from the keys page for your service account. Alternatively, you can directly enter the two values in the above fields',
            )}
          </ChakraText>
          <Button isDisabled={isDisabled} leftIcon={<FiFile />} onClick={onButtonClick}>
            {t('upload_a_file', 'Upload a File')}
          </Button>
          <input
            ref={inputFileRef}
            type="file"
            accept="application/json"
            onChange={event => {
              handleFileChange({ event, form })
            }}
            hidden
          />

          <FormErrorMessage marginBottom="-2.0408em">
            {form.errors.googleKeyFile}
          </FormErrorMessage>
        </FormControl>
      )}
    </Field>
  )
}

export default GoogleKeyFileUpload
